/**
 * @author      carlos.soto@zumper.com (Carlos J. Soto)
 * @copyright   Copyright (c) 2025, Zumper
 */

import { selectLocation } from '@zcode-ui/web-zumper-global-state'
import { aiAssistantSearchFilters } from '@zcode/nr/es/shared/api'
import { push } from '@zcode/nr/es/shared/packages/history-middleware'
import type { AIAssistantSearchResponse, Filters } from '@zcode/state-type'
import { logger } from '@zcode/utils'
import type { O } from 'ts-toolbelt'
import { DEFAULT_FILTERS } from '../../../constants'
import { createUrlFromLocationAndFilter } from '../../../helpers'
import { getReduxFriendlyFilters } from '../../../helpers/filters/getReduxFriendlyFilters'
import type { Dispatch, GetState } from '../../types'
import { setIsZoeSearch, setZoeLocation } from '../actions'
import { handleFilterChange } from './handleFilterChange'

export const getAIAssistantSearchFilters =
  (searchQuery: string) =>
  async (
    dispatch: Dispatch,
    getState: GetState
  ): Promise<AIAssistantSearchResponse | undefined> => {
    const state = getState()
    const location = selectLocation(state)
    const { lat, lng } = location

    if (!lat || !lng) {
      return
    }

    const q = searchQuery.replace(/apartment/gi, 'rental')

    try {
      const aiSearchFilters: AIAssistantSearchResponse = await aiAssistantSearchFilters.query({
        q,
        lat,
        lng,
      })

      if (!aiSearchFilters || Object.keys(aiSearchFilters).length === 0) {
        return
      }

      let mappedAIFilters: O.Writable<Partial<Filters>> = {}
      if (aiSearchFilters.filters) {
        mappedAIFilters = getReduxFriendlyFilters(aiSearchFilters.filters)
      }

      const newFilters: Filters = { ...DEFAULT_FILTERS, ...mappedAIFilters }

      if (aiSearchFilters.location?.url) {
        // We need to omit the box to avoid populating it in the url
        const url = createUrlFromLocationAndFilter({
          filters: newFilters,
          location: {
            ...aiSearchFilters.location,
            box: null,
          },
        })
        dispatch(setZoeLocation(aiSearchFilters.location))
        dispatch(setIsZoeSearch(true))
        dispatch(push(url))
      } else {
        dispatch(handleFilterChange(newFilters, true, true))
      }
      return aiSearchFilters
    } catch (error) {
      logger.error('Error loading Ask Zoe query', error)
      return undefined
    }
  }
